/**
 * Invisible semantic + transcript layer for the homepage cinematic video.
 *
 * Emits a VideoObject JSON-LD with full transcript text and a visually hidden
 * but screen-reader/crawler-accessible block of the same text. The visual
 * modal experience is unaffected.
 */
interface Props {
  /** Public URL of the video file (mp4/webm). */
  videoUrl: string;
  /** Optional poster/thumbnail URL. */
  thumbnailUrl?: string;
  /** ISO 8601 duration like "PT1M20S". Optional. */
  duration?: string;
  /** Locale label for the transcript (e.g. "en"). */
  language?: string;
}

const TRANSCRIPT_PARAGRAPHS = [
  'Say It NOW is a quiet rebellion against waiting too long.',
  'Most of us save our most important words for a eulogy. We rehearse them in our heads on the drive home. We write them in cards we never send. We promise ourselves we’ll say it next time we see them.',
  'But the truth is the people we love are still here, right now. They are answering their phones. They are reading their messages. They are alive.',
  'Say It NOW is a way to write a Living Announcement — a heartfelt message of love, gratitude, apology, or appreciation — and deliver it to someone while they are still here to read it.',
  'You can write it for your mother on her seventieth birthday. For the friend who saved your life at 3am. For the teacher whose one sentence changed everything. For the partner you have never quite told the full truth.',
  'It takes a few minutes. It can last the rest of their life.',
  'Don’t wait for a eulogy to say the thing. Celebrate people while they’re alive. Start your Living Announcement now.',
];

const SHORT_SUMMARY =
  'Brand film for Say It NOW — a platform for sending heartfelt Living Announcements (love, gratitude, apology, family appreciation, and tribute messages) to the people we love while they’re still here to read them.';

export function CinematicVideoSemanticLayer({
  videoUrl,
  thumbnailUrl,
  duration = 'PT1M20S',
  language = 'en',
}: Props) {
  const fullTranscript = TRANSCRIPT_PARAGRAPHS.join(' ');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: 'Say It NOW — Watch Our Story',
    description: SHORT_SUMMARY,
    uploadDate: '2026-03-20',
    duration,
    contentUrl: videoUrl,
    embedUrl: videoUrl,
    thumbnailUrl: thumbnailUrl ?? 'https://say-it-now.app/og/sayitnow-og.png',
    inLanguage: language,
    publisher: {
      '@type': 'Organization',
      name: 'Say It NOW',
      url: 'https://say-it-now.app',
    },
    transcript: fullTranscript,
    keywords: [
      'living announcement',
      'celebrate people while they are alive',
      'heartfelt message',
      'gratitude message',
      'love message',
      'apology message',
      'family appreciation',
      'emotional celebration',
    ].join(', '),
    about: [
      { '@type': 'Thing', name: 'Living Announcement' },
      { '@type': 'Thing', name: 'Emotional celebration' },
      { '@type': 'Thing', name: 'Heartfelt communication' },
    ],
    isAccessibleForFree: true,
    accessibilityFeature: ['transcript', 'captions', 'audioDescription'],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026") }}
      />
      <section
        className="sr-only"
        aria-label="Say It NOW brand film transcript and emotional summary"
        data-semantic-layer="cinematic-video"
      >
        <h2>Say It NOW — Watch Our Story</h2>
        <p>
          <strong>Emotional summary:</strong> {SHORT_SUMMARY}
        </p>
        <h3>Video transcript</h3>
        {TRANSCRIPT_PARAGRAPHS.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
        <p>
          Topics covered: living announcement, gratitude message, love message,
          apology experience, family appreciation, affirmation intent, emotional
          celebration, heartfelt message before it’s too late.
        </p>
      </section>
    </>
  );
}
